import { ReactNode, useState } from 'react';

import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { Rolling } from '@components/animators/rolling';
import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconElement } from '@components/icons/element';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { scrollbarStyle, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { ElementsList } from './elements-list';
import { TreeView } from './tree-view';

const locale = {
  en: {
    pageElements: 'Page elements',
    addElement: 'Add element',
    buttonAdd: 'Add',
    form: 'Form',
  },
};

export const FieldSidebarLabel = ({ label }: { label: string }) => {
  const trimmedLabel = label.trim();
  return (
    <Text
      font="regular14"
      color={trimmedLabel ? 'textPrimary' : 'textHelper'}
      text={trimmedLabel || 'No label'}
    />
  );
};

export const FieldsSidebar = ({ extraFieldGroups }: { extraFieldGroups?: ReactNode }) => {
  const [sideView, setSideView] = useState<'form' | 'elements'>('elements');
  const showForm = sideView === 'form';
  const { t } = useScopedTranslate(locale);

  const SidebarIcon = showForm ? IconElement : IconAddPlus;

  return (
    <LeftSidebar id="left-sidebar">
      <TopContainer>
        <Stack orientation="horizontal" space={2} align="center">
          <SidebarIcon color="elementBlue100" />
          <Rolling variable={showForm}>
            <Text
              font="bold18"
              color="textPrimary"
              text={showForm ? t('pageElements') : t('addElement')}
            />
          </Rolling>
        </Stack>

        {showForm ? (
          <Button
            variant="secondary"
            text={t('buttonAdd')}
            iconLeft={<IconAddPlus />}
            onClick={() => setSideView('elements')}
          />
        ) : (
          <Button
            variant="secondary"
            iconLeft={<IconArrowLeft />}
            text={t('form')}
            onClick={() => setSideView('form')}
          />
        )}
      </TopContainer>
      <Separator />
      <ElementsContainer>
        <AnimatePresence initial={false}>
          {showForm ? (
            <Slide key="elements" offset={30}>
              <TreeView />
            </Slide>
          ) : (
            <ElementsList extraFieldGroups={extraFieldGroups} />
          )}
        </AnimatePresence>
      </ElementsContainer>
    </LeftSidebar>
  );
};

const ElementsContainer = styled.div({
  padding: pxToRem(24),
});

const TopContainer = styled.div({
  padding: `${pxToRem(16)} ${pxToRem(24)}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});

const LeftSidebar = styled.div({
  background: theme.color.backgroundLightDark,
  overflowY: 'auto',
  boxShadow: `${pxToRem(-1)} 0px 0px 0px ${theme.color.border10} inset`,
  ...scrollbarStyle,
});
